import type { useI18n } from "../i18n/useI18n"
import type { ValidatorIssue } from "../schemas"

function getSkillId(issue: ValidatorIssue): string | undefined {
  const match = issue.path?.match(/selectedSkills\[([^\]]+)\]/u)
  return match?.[1]
}

function getNumberPair(
  message: string,
): { first: number; second: number } | null {
  const match = message.match(/(\d+).+?(\d+)/u)
  if (!match) return null
  return {
    first: Number.parseInt(match[1] ?? "", 10),
    second: Number.parseInt(match[2] ?? "", 10),
  }
}

export function getCardValidationIssueLabel(
  t: ReturnType<typeof useI18n>["t"],
  issue: ValidatorIssue,
): string {
  const skillId = getSkillId(issue)

  switch (issue.code) {
    case "CARD_DUPLICATE_SKILL":
      return t("cardEditor.validation.duplicateSkill", {
        skillId: skillId ?? t("common.unknown"),
      })
    case "CARD_UNKNOWN_SKILL":
      return t("cardEditor.validation.unknownSkill", {
        skillId: skillId ?? t("common.unknown"),
      })
    case "CARD_SKILL_AFFINITY_MISMATCH":
      return t("cardEditor.validation.skillAffinityMismatch", {
        skillId: skillId ?? t("common.unknown"),
      })
    case "CARD_MODIFIER_NOT_AVAILABLE":
      if (issue.path?.endsWith(".allyRequirement")) {
        return t("cardEditor.validation.allyRequirementUnavailable", {
          skillId: skillId ?? t("common.unknown"),
        })
      }
      if (issue.message.includes("self_destruct")) {
        return t("cardEditor.validation.selfDestructUnavailable", {
          skillId: skillId ?? t("common.unknown"),
        })
      }
      return t("cardEditor.validation.nonExhaustingUnavailable", {
        skillId: skillId ?? t("common.unknown"),
      })
    case "CARD_DUPLICATE_COLOR":
      return t("cardEditor.validation.duplicateColor")
    case "CARD_SURCHARGE_MISSING":
      return t("cardEditor.validation.surchargeMissing")
    case "CARD_SURCHARGE_UNEXPECTED":
      return t("cardEditor.validation.surchargeUnexpected")
    case "CARD_COST_EXCEEDS_CAP": {
      const numbers = getNumberPair(issue.message)
      return numbers
        ? t("cardEditor.validation.costExceedsCap", {
            cost: numbers.first,
            max: numbers.second,
          })
        : issue.message
    }
    case "CARD_DERIVED_COST_STALE": {
      const numbers = getNumberPair(issue.message)
      return numbers
        ? t("cardEditor.validation.derivedCostStale", {
            stored: numbers.first,
            computed: numbers.second,
          })
        : issue.message
    }
    default:
      return issue.message
  }
}
